import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const sessionVersion = useRef(0);

  // Resolve the current session from the httpOnly cookie (no token in JS storage).
  const refresh = useCallback(async () => {
    const version = sessionVersion.current;
    try {
      const r = await axios.get(`${API}/nationals/me`);
      if (version === sessionVersion.current) setUser(r.data);
      return r.data;
    } catch (error) {
      // A dropped network connection must not impersonate a revoked login.
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        if (version === sessionVersion.current) setUser(null);
      }
      return null;
    } finally {
      if (version === sessionVersion.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // HTTP-only sessions can be cleared in the government site while a Live
    // tab stays mounted. Revalidate rather than trusting a stale React user.
    const timer = window.setInterval(refresh, 15000);
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      /* ignore — cookies are cleared server-side regardless */
    }
    sessionVersion.current += 1;
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, refresh, logout, setUser, isAuthenticated: !!user }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
